import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Alert} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {sectionCardSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {int, kg, pct} from '../format'
import {chargeableKg} from './catalogueMath'
import KeyValue from '../components/KeyValue'

/**
 * The sub-category actually saved: `sub` when it is still one of `cat`'s
 * options, else the first option under `cat`. The Category select already
 * keeps this satisfied on every change (it resets `sub` itself — see the
 * drawer's own `handleCatChange`), but `subcats` is store-owned and could in
 * principle change under an already-open drawer, so Save re-applies the
 * exact same guard as a backstop rather than trusting the local `sub` state
 * blindly. Pure and exported so this guard is unit-tested without mounting
 * the drawer.
 */
export function resolveReviewSub(subcats, cat, sub) {
  const options = (subcats && subcats[cat]) || []
  return options.indexOf(sub) >= 0 ? sub : (options[0] || '')
}

/**
 * The volumetric weight a chosen packing material would anchor to: the
 * pack's own `volKg` when it has one (a dimensioned carton), else the SKU's
 * existing `volKg` (a flyer or paper wrap carries none — see PACKS in
 * noberoFreeze.js). This is exactly the fallback `catalogueState.applyReview`
 * uses when the save actually lands, kept here as its own pure function so
 * the live preview line below can never compute a different number than
 * what Save is about to persist.
 */
export function packVolFor(pack, sku) {
  return pack && pack.volKg != null ? pack.volKg : (sku ? sku.volKg : 0)
}

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}

/**
 * Review drawer for an auto-added SKU — Catalogue Task 7. `sku` is the live
 * store record threaded down from CataloguePage (resolved fresh off `skus`
 * by code every render, exactly like MergeDrawer's own `list`), never copied
 * into this drawer's own state except for the three fields the merchant is
 * actively editing (`cat`/`sub`/`packId`) — every number shown (shipments,
 * most probable weight, spread, sorter coverage) is read straight off it.
 *
 * `subcats`/`packs` are the SAME store-owned arrays CataloguePage already
 * threads to the rest of Catalogue (see useCatalogueState's own `packs`, and
 * the store's `subcats`) — not a second, static import of noberoFreeze's
 * SUBCATS/PACKS — so a future Packing edit to either would be reflected here
 * exactly like everywhere else in this section.
 *
 * Keyboard-dismissable and focus-return are handled by MUI's underlying
 * Modal (Escape key, backdrop click, restore focus to the trigger), exactly
 * like MergeDrawer/UploadDrawer; CataloguePage's own `closeReviewDrawer`
 * additionally falls back to focusing the active rail item if that restore
 * ever lands on `<body>` — the SAME `focusFallbackRef` fallback those two
 * drawers already use, reused rather than re-invented here.
 */
export default function ReviewSkuDrawer({open, sku, subcats, packs, onClose, onSave, onNavigate}) {
  const s = sku || {}

  const [cat, setCat] = React.useState(s.cat || '')
  const [sub, setSub] = React.useState(s.sub || '')
  const [packId, setPackId] = React.useState(s.packId != null ? s.packId : null)

  // Reset to the SKU's own current fields on the closed -> open transition,
  // not on every render — the same "adjusted during render" pattern
  // MergeDrawer/UploadDrawer use to avoid a stale pick carrying over from
  // the last time this same component instance was shown.
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setCat(s.cat || '')
      setSub(s.sub || '')
      setPackId(s.packId != null ? s.packId : null)
    }
  }

  const catOptions = React.useMemo(() => Object.keys(subcats || {}).sort(), [subcats])
  const subOptions = (subcats && subcats[cat]) || []
  const pack = (packs || []).find((p) => p.id === packId) || null
  const packVol = packVolFor(pack, s)
  const previewCharge = chargeableKg(s.deadKg, packVol)

  const handleCatChange = (e) => {
    const next = e.target.value
    setCat(next)
    setSub(((subcats && subcats[next]) || [])[0] || '')
  }

  const handleSave = () => {
    if (!s.sku) return
    onSave(s.sku, {cat, sub: resolveReviewSub(subcats, cat, sub), packId})
  }

  const handleNavigate = () => {
    onClose()
    onNavigate('initiate')
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 560}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Review auto-added SKU
              </Typography>
              <Typography sx={{...labelSx, fontFamily: 'monospace', wordBreak: 'break-all'}}>{s.sku}</Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close review drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Alert severity='warning' sx={alertSx}>
            This code was added automatically because it shipped without being in the catalogue. It keeps
            shipping either way, but it cannot be frozen until its sub-category and packing material are
            confirmed.
          </Alert>

          <Box sx={sectionCardSx}>
            <KeyValue items={[
              {label: 'Shipments', value: int(s.ships)},
              {label: 'Most probable weight', value: kg(s.mpw)},
              {label: 'Weight spread', value: pct(s.spread)},
              {label: 'Shipments with a courier sorter reading', value: `${int(s.sorterCount)} of ${int(s.ships)}`},
            ]} />
          </Box>

          <TextField
            select
            size='small'
            label='Category'
            value={cat}
            onChange={handleCatChange}
            sx={{fontFamily: FONT, fontSize: SIZE.xs}}
          >
            {catOptions.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size='small'
            label='Sub-category'
            value={subOptions.indexOf(sub) >= 0 ? sub : ''}
            onChange={(e) => setSub(e.target.value)}
            helperText='Weight freeze is agreed at this level by default.'
            sx={{fontFamily: FONT, fontSize: SIZE.xs}}
          >
            {subOptions.map((sc) => (
              <MenuItem key={sc} value={sc}>{sc}</MenuItem>
            ))}
          </TextField>

          <Box>
            <TextField
              select
              fullWidth
              size='small'
              label='Default packing material'
              value={packId || ''}
              onChange={(e) => setPackId(e.target.value)}
              helperText='Volumetric weight is anchored to this.'
              sx={{fontFamily: FONT, fontSize: SIZE.xs}}
            >
              {(packs || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
              ))}
            </TextField>
            <Typography sx={{...labelSx, mt: 0.75}}>
              Chargeable weight would be {kg(previewCharge)}
            </Typography>
          </Box>

          <Alert severity='info' sx={alertSx}>
            Once reviewed, this SKU can be taken into a freeze request from{' '}
            <Button
              variant='text'
              size='small'
              onClick={handleNavigate}
              sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE, p: 0, minWidth: 0, verticalAlign: 'baseline'}}
            >
              initiate weight freeze
            </Button>.
          </Alert>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={primaryBtnSx} onClick={handleSave}>Confirm and mark reviewed</Button>
        </Box>
      </Box>
    </Drawer>
  )
}
